import React from 'react';
import { Link } from 'react-router-dom';
import { PortalDataState } from '../components/PortalDataState';
import { PortalStatusPill } from '../components/PortalStatusPill';
import type { CollaborationOverviewModel, CollaborationExperienceTab } from './collaborationOverviewLogic';

interface CollaborationOverviewProps {
  model: CollaborationOverviewModel;
  selectedTenantId: string;
  invitationEvidenceIncomplete: boolean;
  onNavigate: (tab: CollaborationExperienceTab) => void;
  /**
   * Tracker #121: supplier set-up and governance belong to collaboration
   * administrators. Anyone else sees only their own work: no supplier set-up
   * prompts, no "Needs attention" list and no supplier readiness.
   */
  canManage?: boolean;
}

const CollaborationOverview = ({
  model,
  selectedTenantId,
  invitationEvidenceIncomplete,
  onNavigate,
  canManage = true
}: CollaborationOverviewProps): JSX.Element => {
  const hasSuppliers = model.totalSupplierCount > 0;

  if (!canManage) {
    return (
      <div className="collaboration-overview">
        <p className="muted">
          You see the supplier collaborations you started, and those for Findings or Audits you lead. To start one,
          open the Finding in Edinshall Audit in SharePoint and request a supplier response.
        </p>
        <div className="collaboration-summary-grid">
          {model.newSupplierActivityCount > 0 && (
            <button
              className="card collaboration-summary-card collaboration-summary-card--attention"
              type="button"
              onClick={() => onNavigate(model.newReceivedActivityCount > 0 ? 'inbound' : 'outbound')}
            >
              <span className="collaboration-summary-card__label">New supplier activity</span>
              <strong>{model.newSupplierActivityCount} thread{model.newSupplierActivityCount === 1 ? '' : 's'}</strong>
              <span className="muted">Open Sent or Received work to review the highlighted threads</span>
            </button>
          )}
          <button className="card collaboration-summary-card" type="button" onClick={() => onNavigate('outbound')}>
            <span className="collaboration-summary-card__label">Sent work</span>
            <strong>{model.openSentCount} open</strong>
            <span className="muted">Your collaborations with suppliers</span>
          </button>
          <button className="card collaboration-summary-card" type="button" onClick={() => onNavigate('inbound')}>
            <span className="collaboration-summary-card__label">Received work</span>
            <strong>{model.receivedReviewCount} current</strong>
            <span className="muted">Work received from another organisation that names you</span>
          </button>
        </div>
      </div>
    );
  }

  if (!hasSuppliers) {
    return (
      <PortalDataState
        kind="first-empty"
        heading="Add your first supplier"
        message="Record the supplier organisation, add the person who will administer collaboration, then guide them through Microsoft sign-in and acceptance."
        action={(
          <button className="btn" type="button" onClick={() => onNavigate('partners')}>
            Add supplier
          </button>
        )}
      />
    );
  }

  return (
    <div className="collaboration-overview">
      <section className="card collaboration-attention">
        <div className="collaboration-section-heading">
          <div>
            <h3>Needs attention</h3>
            <p className="muted">Current setup and governance actions, ordered before general activity.</p>
          </div>
          <PortalStatusPill
            label={model.attention.length === 0 ? 'Nothing needs attention' : `${model.attention.length} open`}
            tone={model.attention.length === 0 ? 'success' : 'warning'}
          />
        </div>
        {model.attention.length === 0 ? (
          <div className="collaboration-quiet-success">Supplier setup and current collaboration checks are clear.</div>
        ) : (
          <div className="collaboration-attention-list">
            {model.attention.map((item) => {
              const content = (
                <>
                  <span>
                    <strong>{item.title}</strong>
                    <span className="muted collaboration-attention-detail">{item.detail}</span>
                  </span>
                  <PortalStatusPill label="Review" tone={item.tone} />
                </>
              );
              return item.partnerId ? (
                <Link
                  key={item.id}
                  className="collaboration-attention-row"
                  to={`/collaboration/partners/${encodeURIComponent(item.partnerId)}?tenantId=${encodeURIComponent(selectedTenantId)}`}
                >
                  {content}
                </Link>
              ) : (
                <button
                  key={item.id}
                  className="collaboration-attention-row"
                  type="button"
                  onClick={() => onNavigate(item.tab)}
                >
                  {content}
                </button>
              );
            })}
          </div>
        )}
        {invitationEvidenceIncomplete && (
          <p className="muted collaboration-evidence-note">
            Some invitation timing could not be checked. Open the relevant Supplier Profile for its current history.
          </p>
        )}
      </section>

      <div className="collaboration-summary-grid">
        {model.newSupplierActivityCount > 0 && (
          <button
            className="card collaboration-summary-card collaboration-summary-card--attention"
            type="button"
            onClick={() => onNavigate(model.newReceivedActivityCount > 0 ? 'inbound' : 'outbound')}
          >
            <span className="collaboration-summary-card__label">New supplier activity</span>
            <strong>{model.newSupplierActivityCount} thread{model.newSupplierActivityCount === 1 ? '' : 's'}</strong>
            <span className="muted">Open Sent or Received work to review the highlighted threads</span>
          </button>
        )}
        <button className="card collaboration-summary-card" type="button" onClick={() => onNavigate('partners')}>
          <span className="collaboration-summary-card__label">Supplier readiness</span>
          <strong>{model.activeSupplierCount} active</strong>
          <span className="muted">{model.setupSupplierCount} still being set up</span>
        </button>
        <button className="card collaboration-summary-card" type="button" onClick={() => onNavigate('outbound')}>
          <span className="collaboration-summary-card__label">Sent work</span>
          <strong>{model.openSentCount} open</strong>
          <span className="muted">Work initiated by your auditors</span>
        </button>
        <button className="card collaboration-summary-card" type="button" onClick={() => onNavigate('inbound')}>
          <span className="collaboration-summary-card__label">Received work</span>
          <strong>{model.receivedReviewCount} current</strong>
          <span className="muted">Work received from another organisation</span>
        </button>
      </div>
    </div>
  );
};

export default CollaborationOverview;
